import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { debounceTime, distinctUntilChanged, fromEvent, map, Observable, of, Subject, switchMap } from 'rxjs';
import { DynacrudApiWrapper } from '../../api/dynacrud-api';
import { ControlValueAccessorBaseImpl } from '../control-value/ControlValueAccessorBaseImpl';

import { isNullOrUndefined } from 'is-what';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { SubSink } from 'subsink';
import _ from 'lodash';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { ASCIIFolder } from 'src/app/shared/models/entities';
import { localizedName } from 'src/app/shared/i18n/localized-name';
import { AppLocaleService } from 'src/app/shared/services/app-locale.service';
import { ClosedSelectPick } from 'src/app/shared/utils/closed-select-pick';


export const DEFAULT_SELECT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DefaultSelectComponent),
  multi: true
};

@Component({
  selector: 'app-default-select',
  templateUrl: './default-select.component.html',
  styleUrls: ['./default-select.component.scss'],
  providers: [DEFAULT_SELECT_CONTROL_VALUE_ACCESSOR]
})
export class DefaultSelectComponent extends ControlValueAccessorBaseImpl<any> implements OnInit, OnDestroy {

  @Input()
  label!: string;

  @Input()
  idFilter!: number;

  @Input()
  source!: string;

  @Input()
  isRequiredVal!: boolean;

  @Input()
  multiple!: boolean;

  @Input()
  filterBy!: CallableFunction;

  @Input()
  modifyData!: CallableFunction;

  items!: any[];

  @Input() hideHint!: boolean;

  @Input()
  override readonly!: boolean;

  @Input()
  canFilter: boolean = true;

  @Input()
  featureCode: string | undefined;


  @Input()
  disabledClear!: boolean;

  @Input()
  disabledItemAllButton!: boolean;





  /**
   * Single select only. Off, every pick reaches the form at once, as `[(ngModel)]` did. On, a pick
   * made on the CLOSED select -- where every arrow key and type-ahead letter is one -- reaches it
   * only on blur, on Enter or when the panel closes, so a host whose change runs a search searches
   * once for a walk of the list, not once per key (MR !217 review). A pick from the open panel is
   * immediate either way. A pick still held when the select is destroyed is dropped: this is a form
   * value, and its page is going away with it. Wired by `[appSettleClosedPick]` in the template.
   *
   * On, `[debounce]` no longer applies to a pick: settling already decides when a pick reaches the
   * form, and it does so synchronously on purpose (see `commitSettledPick`). Do not set both.
   */
  @Input()
  settleClosedPicks = false;

  readonly singlePick = new ClosedSelectPick<any>((v) => this.commitSettledPick(v));

  onSingleModelChange(v: any): void {
    if (!this.settleClosedPicks) {
      this.value = v;
    }
  }

  /**
   * A settled pick reaches the form SYNCHRONOUSLY, not through the base's `debounceTime` subject.
   * Enter settles on its keydown, and new-movement saves on the same Enter's keyup: through the
   * subject the form heard about the pick a macrotask later, so a keyup arriving first -- keys sent
   * back to back, a busy main thread -- saved the value from before the walk while the select showed
   * the new one (MR !217 review).
   */
  private commitSettledPick(v: any): void {
    if (_.isEqual(v, this.$value)) {
      return;
    }
    this.$value = v;
    this.onChangeCallback(v);
    this.onTouchedCallback();
  }

  subs = new SubSink();
  itemsMemory: any[] = [];
  disabledAllDaPulsante = false;

  constructor(private dynacrudApiWrapper: DynacrudApiWrapper, private localeService: AppLocaleService) { super(); }

  override ngOnDestroy(): void {
    // Dropped, as the input's doc says: this is a form value and its page is going away with it.
    this.singlePick.discard();
    super.ngOnDestroy();
    this.subs.unsubscribe();
  }

  @ViewChild('valueNgxSelectSearch') valueNgxSelectSearch: MatSelectSearchComponent | undefined;

  pasteValue(event: ClipboardEvent) {
    const value: string | undefined = event.clipboardData?.getData('text');
    const textSearch = ASCIIFolder.foldReplacing(value);
    if (this.valueNgxSelectSearch) {

      of(textSearch).pipe(
        distinctUntilChanged(),
        debounceTime(300),
        map((d: string) => {

          if (d && d.length > 2) {
            return d;
          }

          return undefined;

        })).subscribe(res => {
          if (res != undefined) {

            this.filterMyOptions(res as string);
          }
        });
    }
  }

  override ngOnInit() {
    super.ngOnInit();
    // potremmo dover passare per gli stati... FIXME K3 ragioanci bene

    this.dynacrudApiWrapper.getFor(this.source).search({ pagination: { size: 10000, page: 0 } }).pipe(map(ret => {
      let retdata = ret.data;

      if (this.filterBy) {
        retdata = retdata.filter((item: any) => this.filterBy(item));
      }

      if (this.modifyData) {
        retdata = this.modifyData(retdata);
      }

      if (this.idFilter) {
        const valueOn = retdata.find((item: any) => item.id === this.idFilter);
        if (valueOn) {
          this.value = valueOn;
        }
      }

      return retdata;
    })).subscribe(d => {
      this.items = d || [] ;
      this.itemsMemory = _.cloneDeep(this.items);
      this.reconcileSelection();
    });

    setTimeout(() => {

      if (this.valueNgxSelectSearch) {
        fromEvent(this.valueNgxSelectSearch.searchSelectInput.nativeElement, 'keyup').pipe(
          distinctUntilChanged(),
          debounceTime(300),
          map((d: any) => {

            if (d.target.value) {

              if ((d.target.value as string).length > 2) {
                const value: string = d.target.value;
                const textSearch = ASCIIFolder.foldReplacing(value);

                if (textSearch && textSearch.length > 2) {

                  return textSearch;

                }

                return undefined;
              }

            }

          })).subscribe(res => {
            if (res && res != undefined) {
              this.filterMyOptions(res as string);

            }
          })


      }

    }, 2000);
  }

  override writeValue(obj: any): void {
    // A value written from outside while a pick is held -- a form reset, a restore -- replaces the
    // walk: the select now shows the written value, and committing the walk on the next blur would
    // put back a value the user can no longer see (MR !217 review).
    if (!_.isEqual(obj, this.$value)) {
      this.singlePick.discard();
    }
    super.writeValue(obj);
    // A persisted selection (restored from the store/localStorage) may reference
    // entities that are no longer in the loaded option set — e.g. warehouse
    // sessions that are no longer pending and thus dropped by the
    // `sessions/working` response. Reconcile so the trigger count and mat-hint
    // reflect only options that actually exist. Runs again once options load.
    this.reconcileSelection();
  }

  /**
   * For a multiple select, drop any selected entity that no longer matches a
   * loaded option (compared by `compareEntity`, i.e. by id). Emits the pruned
   * value through the setter so the cleaned selection propagates back to the
   * form control (and, in turn, the persisted store state). No-op until the
   * options have loaded (`items` becomes an array only after the HTTP call).
   */
  reconcileSelection(): void {
    if (!this.multiple || !Array.isArray(this.items)) {
      return;
    }
    const current = this.value as any[] | undefined;
    if (!Array.isArray(current) || !current.length) {
      return;
    }
    const pruned = current.filter(sel => this.items.some(item => this.compareEntity(sel, item)));
    if (pruned.length !== current.length) {
      this.value = pruned;
    }
  }

  showValue(item: any) {
    if (item) {
      return localizedName(item, this.localeService.current) || item.description || item.code || item.id || item;
    }
    return item;
  }

  getFloor(x: any) {
    return Math.floor(x);
  }



  getPositionSprite(i: any): { x?: number, y?: number } {
    if (i) {
      const x = this.getFloor((i % 10)) * 21;
      const y = this.getFloor((i / 10)) * 21;
      return { x: x, y: y };
    }

    return { x: undefined, y: undefined }
  }


  isOpenBro(ev: any) {

    if (ev === false) {

      this.itemsMemory = _.cloneDeep(this.items);
    }

  }

  filterMyOptions(v: string | undefined): void {

    this.itemsMemory = _.cloneDeep(this.items);
    const cacheItems = _.cloneDeep(this.items);

    if (!v) {
      v = '';
    }

    this.itemsMemory = cacheItems.filter(d => (d.name as string).toLowerCase().includes((v as string).toLowerCase()));
  }



  clearValues() {

    this.value = [];
  }

  public compareEntity(o: any, o2: any) {
    if (isNullOrUndefined(o) && isNullOrUndefined(o2)) {
      return true;
    }

    if ((isNullOrUndefined(o) && !isNullOrUndefined(o2)) || (isNullOrUndefined(o2) && !isNullOrUndefined(o))) {
      return false;
    }

    return o === o2 || o.id === o2.id;
  }

  selectAllFiltered(matSelect: MatSelect) {
    const m = [...matSelect.options];
    let sum = 0;
    let a = 0;
    m.map((d, i) => {
      a += i;
      return a;
    });


    matSelect.options.forEach((item: MatOption, i) => {

      if (item && item.value) {
        item.select();
        if (item.selected) {
          sum += i;
        }
      }
    });



  }

  deselectAllFiltered(matSelect: MatSelect) {
    matSelect.options.forEach((item: MatOption) => {
      if (item && item.value) {
        item.deselect();
      }
    });
  }

  hasFilteredSelection(): boolean {
    const selected = this.value as any[] | undefined;
    if (!selected?.length || !this.itemsMemory?.length) {
      return false;
    }
    return this.itemsMemory.some(item => selected.some(s => this.compareEntity(s, item)));
  }





}


